import React from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

// --- Types (T001, T002) ---

interface ProjectionConfig {
  center: [number, number];
  scale: number;
}

interface CountryGeoConfig {
  /** CDN URL of the TopoJSON file for this country */
  geoUrl: string;
  /** TopoJSON object key (e.g., "can", "pol"); undefined uses first object (US atlas default) */
  objectKey?: string;
  /** Maps TopoJSON properties.name → app display name when they differ */
  nameMapping: Record<string, string>;
  /** Per-region projection configs, keyed by app display name */
  projectionConfigs: Record<string, ProjectionConfig>;
  /** Fallback projection when region not found */
  defaultProjection: ProjectionConfig;
}

interface StateShapeProps {
  /** Display name of the region to highlight (e.g., "Texas", "Ontario", "Masovia") */
  stateName: string;
  /** Country slug — determines which TopoJSON source to load. Defaults to "united-states". */
  country?: string;
  /** Fill color for the highlighted region. Default: "#FFD700" (gold) */
  color?: string;
  /** Pixel dimensions of the square map container. Default: 300 */
  size?: number;
}

// --- Country Geo Configurations (T002, T009-T042) ---

const COUNTRY_GEO_CONFIGS: Record<string, CountryGeoConfig> = {
  'united-states': {
    geoUrl: 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json',
    objectKey: undefined,
    nameMapping: {},
    projectionConfigs: {
      Alabama: { center: [-86.8, 32.8], scale: 6000 },
      Alaska: { center: [-153, 64], scale: 1400 },
      Arizona: { center: [-111.5, 34.5], scale: 4500 },
      Arkansas: { center: [-92.5, 34.8], scale: 5500 },
      California: { center: [-119.5, 37], scale: 2800 },
      Colorado: { center: [-105.5, 39], scale: 4500 },
      Connecticut: { center: [-72.7, 41.6], scale: 15000 },
      Delaware: { center: [-75.5, 39], scale: 16000 },
      Florida: { center: [-81.5, 28], scale: 5000 },
      Georgia: { center: [-83.5, 32.7], scale: 6000 },
      Hawaii: { center: [-156.3, 20.5], scale: 5500 },
      Idaho: { center: [-114.5, 44.5], scale: 3700 },
      Illinois: { center: [-89.5, 40], scale: 5000 },
      Indiana: { center: [-86.3, 40], scale: 7000 },
      Iowa: { center: [-93.5, 42], scale: 5500 },
      Kansas: { center: [-98.5, 38.5], scale: 5000 },
      Kentucky: { center: [-85.5, 37.8], scale: 6000 },
      Louisiana: { center: [-92, 31], scale: 5500 },
      Maine: { center: [-69, 45.5], scale: 5000 },
      Maryland: { center: [-76.8, 39], scale: 11000 },
      Massachusetts: { center: [-71.5, 42.3], scale: 13000 },
      Michigan: { center: [-85, 44.5], scale: 4000 },
      Minnesota: { center: [-94.5, 46], scale: 4000 },
      Mississippi: { center: [-89.5, 32.7], scale: 6000 },
      Missouri: { center: [-92.5, 38.5], scale: 5000 },
      Montana: { center: [-109.5, 47], scale: 3200 },
      Nebraska: { center: [-99.8, 41.5], scale: 4500 },
      Nevada: { center: [-117, 39.5], scale: 3500 },
      'New Hampshire': { center: [-71.5, 43.7], scale: 11000 },
      'New Jersey': { center: [-74.5, 40.1], scale: 11000 },
      'New Mexico': { center: [-106, 34.5], scale: 4500 },
      'New York': { center: [-75.5, 43], scale: 5000 },
      'North Carolina': { center: [-79.5, 35.5], scale: 5000 },
      'North Dakota': { center: [-100.5, 47.5], scale: 4500 },
      Ohio: { center: [-82.5, 40.5], scale: 6500 },
      Oklahoma: { center: [-97.5, 35.5], scale: 5000 },
      Oregon: { center: [-120.5, 44], scale: 4000 },
      Pennsylvania: { center: [-77.5, 41], scale: 6000 },
      'Rhode Island': { center: [-71.5, 41.7], scale: 27000 },
      'South Carolina': { center: [-81, 34], scale: 7500 },
      'South Dakota': { center: [-100.3, 44.5], scale: 4500 },
      Tennessee: { center: [-86.5, 35.8], scale: 5500 },
      Texas: { center: [-99.9, 31.5], scale: 2200 },
      Utah: { center: [-111.5, 39.3], scale: 5000 },
      Vermont: { center: [-72.7, 44], scale: 11000 },
      Virginia: { center: [-78.5, 37.5], scale: 5500 },
      Washington: { center: [-120.5, 47.5], scale: 5000 },
      'West Virginia': { center: [-80.5, 38.6], scale: 7500 },
      Wisconsin: { center: [-89.5, 44.5], scale: 5000 },
      Wyoming: { center: [-107.5, 43], scale: 4500 },
    },
    defaultProjection: { center: [-96, 38], scale: 1000 },
  },

  // T009-T023: Canadian provinces/territories
  canada: {
    geoUrl: 'https://cdn.jsdelivr.net/npm/datamaps@0.5.10/src/js/data/can.topo.json',
    objectKey: 'can',
    // T009: TopoJSON name → app name (1 mismatch)
    nameMapping: {
      Québec: 'Quebec',
    },
    // Manually tuned projection configs for 6 Canadian provinces (rural/sparse territories removed)
    projectionConfigs: {
      Alberta: { center: [-115.0, 54.0], scale: 1800 },
      'British Columbia': { center: [-125.5, 54.5], scale: 1600 },
      'Nova Scotia': { center: [-63.0, 44.7], scale: 10000 },
      Ontario: { center: [-85.3, 50.0], scale: 1800 },
      'Prince Edward Island': { center: [-63.4, 46.3], scale: 20000 },
      Quebec: { center: [-71.8, 52.0], scale: 1500 },
    },
    // T023: Default fallback for Canada
    defaultProjection: { center: [-96, 62], scale: 800 },
  },

  // T025-T042: Polish voivodeships
  poland: {
    geoUrl: 'https://cdn.jsdelivr.net/npm/datamaps@0.5.10/src/js/data/pol.topo.json',
    objectKey: 'pol',
    // T025: TopoJSON name → app name (11 mismatches)
    nameMapping: {
      Świętokrzyskie: 'Holy Cross',
      'Kuyavian-Pomeranian': 'Kuyavia-Pomerania',
      'Lower Silesian': 'Lower Silesia',
      Masovian: 'Masovia',
      Podlachian: 'Podlasie',
      Pomeranian: 'Pomerania',
      Silesian: 'Silesia',
      Subcarpathian: 'Subcarpathia',
      'Warmian-Masurian': 'Warmia-Masuria',
      'West Pomeranian': 'West Pomerania',
    },
    // T026-T041: Manually tuned projection configs for all 16 voivodeships
    projectionConfigs: {
      'Greater Poland': { center: [17.0, 52.0], scale: 8000 },
      'Holy Cross': { center: [20.7, 50.9], scale: 14000 },
      'Kuyavia-Pomerania': { center: [18.4, 53.1], scale: 10000 },
      'Lesser Poland': { center: [20.1, 49.8], scale: 10000 },
      'Lower Silesia': { center: [16.9, 51.1], scale: 9000 },
      Lublin: { center: [22.6, 51.2], scale: 8000 },
      Lubusz: { center: [15.3, 52.2], scale: 11000 },
      Łódź: { center: [19.5, 51.6], scale: 10000 },
      Masovia: { center: [20.6, 51.8], scale: 7000 },
      Opole: { center: [17.9, 50.6], scale: 14000 },
      Podlasie: { center: [22.9, 53.2], scale: 8000 },
      Pomerania: { center: [18.0, 54.2], scale: 9000 },
      Silesia: { center: [19.1, 50.3], scale: 12000 },
      Subcarpathia: { center: [22.0, 49.8], scale: 10000 },
      'Warmia-Masuria': { center: [20.5, 53.9], scale: 8000 },
      'West Pomerania': { center: [15.4, 53.5], scale: 8000 },
    },
    // T042: Default fallback for Poland
    defaultProjection: { center: [19.5, 52.0], scale: 4000 },
  },

  // United Kingdom nations/jurisdictions (T045–T050: self-hosted Natural Earth admin-0 subunits)
  'united-kingdom': {
    geoUrl: '/geo/uk-nations.topo.json',
    objectKey: 'nations',
    nameMapping: {
      England: 'England and Wales',
      Wales: 'England and Wales',
    },
    projectionConfigs: {
      'England and Wales': { center: [-1.5, 52.5], scale: 3500 },
      Scotland: { center: [-4.2, 57.0], scale: 4500 },
      'Northern Ireland': { center: [-6.9, 54.6], scale: 12000 },
    },
    defaultProjection: { center: [-3.5, 55.0], scale: 2500 },
  },
};

// --- Helper Functions (T004, T006) ---

/**
 * Resolves a TopoJSON geo.properties.name to the app display name
 * by applying the country's nameMapping (direction: TopoJSON name → app name).
 * If no mapping exists, returns the original geo name unchanged.
 */
const resolveGeoName = (geoName: string, config: CountryGeoConfig): string => {
  return config.nameMapping[geoName] || geoName;
};

// --- Component (T003, T005, T007) ---

const StateShape: React.FC<StateShapeProps> = ({
  stateName,
  country = 'united-states',
  color = '#FFD700',
  size = 300,
}) => {
  // Look up country config; render empty container for unrecognized countries
  const config = COUNTRY_GEO_CONFIGS[country];
  if (!config) {
    return <div style={{ width: size, height: size, margin: '0 auto' }} />;
  }

  const projectionConfig = config.projectionConfigs[stateName] || config.defaultProjection;

  return (
    <div style={{ width: size, height: size, margin: '0 auto' }}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={projectionConfig}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={config.geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const resolvedName = resolveGeoName(geo.properties.name, config);
              const isTarget = resolvedName === stateName;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isTarget ? color : 'transparent'}
                  stroke={isTarget ? '#1a1a1a' : 'transparent'}
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
};

export default StateShape;
